import types from './types';
import axios from 'axios';
import { appOperations } from '../../../../App/store';
import ColoredSnackbarConstants from '../../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import { uploadFileToBlobStorage, extractAudioDuration, createBlobFromUrl, convertToMegabytes } from '../../../../utilities/fileUtilities';
import { requestWithRetry } from '../../../../utilities/requestRetryUtilities';
import CreatePageConstants, { ASSET_UPLOADING_MODE, DEFAULT_AUDIO_TRACK, MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, MEDIA_USAGE_TYPE, VIDEO_SAVING_STEP_STATUS } from '../CreatePageConstants';
import { videoActions } from '../../VideosPage/store';
import { trackEvent, AnalyticsConstants, trackResourceSavingFailedEvent } from '../../../../utilities/analyticsUtilities';
import { exportsActions } from '../../ExportsPage/store';
import { PixiRendererInstance } from '../Preview/PreviewDisplay';
import { extractEmojiAndConvertToBase64, getThumbnailSnapshot, getMediaDimensions } from '../../../../utilities/graphicUtilities';
import { findSpaceForSegment, getMiddlePointBounds, convertToLyricsTime } from '../../../../utilities/lyricsUtilities';
import { isBrowserLocalUrl, setBrowserUnload, sleep, delay } from '../../../../utilities/browserUtilities';
import { previewHistoryFilter, directUploadTracker } from './extensions';
import { getLastLogoUpdateStamp, getLastBackgroundUpdateStamp, saveLastLogoUpdateStamp, saveLastBackgroundUpdateStamp, checkAndTriggerVideoSettingsFailure, saveOptimizationMode, saveEditHistoryEnabled, saveAssetUploadingMode, getPreOptimizationSettings, savePreOptimizationSettings, clearPreOptimizationSettings } from '../../../../utilities/storageUtilities';
import { createObjectCopy } from '../../../../utilities/objectUtilities';
import { createMediaSourceDataByType, getMediaSourceSingleUrl, createLocalMediaSourceUrls, isColorOrGradientSource } from '../helpers';
import { generateGuid, randomString } from '../../../../utilities/randomUtilities';
import AppConstants from '../../../../App/AppConstants';
import { EXPORT_STATUSES } from '../../../../constants/ExportStatuses';
import { analyzeCumulativeMaxMagnitudes } from '../Audio/MaxMagnitudeAnalyzer';
import { updateDisplayLoaderPercentage } from '../Preview/PreviewDisplayLoaders';
import { REFLECTION_TYPE, BACKGROUND_REFLECTION_TYPE, BAR_COUNT_STEP } from '../Preview/PreviewDisplayConstants';
import api from '../../../../App/api';
import { DEFAULT_VIDEO_SAVING_STATE } from './reducer';

// ERRORS

class SavingAbortedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SavingAbortedError';
    }
}

// HELPERS

const isNetworkError = (error) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return true;

    if (!error || error.response)
        return false;

    return (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error' ||
        Boolean(error.request)
    );
};

const getSavingFailureMessage = (error, resourceLabel, genericMessage) =>
    isNetworkError(error)
        ? `Couldn't save ${resourceLabel}. Please check your connection and try again.`
        : genericMessage;

// Standardized error handling for the resource saving steps: cancellations mark the step
// as cancelled without tracking, other errors go through the retry dialog and are tracked
// to analytics with the diagnostic context (failureStage, sizeMb, etc.).
const handleResourceSavingError = (dispatch, error, options) => {
    const { stepName, resourceLabel, resourceName, genericMessage, retryAction, onFailed, context, nextSavingHandler } = options;

    console.error(error);

    if (axios.isCancel(error) || error instanceof SavingAbortedError) {
        dispatch(updateSavingStepStatus(stepName, VIDEO_SAVING_STEP_STATUS.CANCELLED, "", undefined));
        nextSavingHandler();
        return;
    }

    dispatch(appOperations.failRequest(error, retryAction, () => {
        if (onFailed)
            onFailed();

        dispatch(updateSavingStepStatus(stepName, VIDEO_SAVING_STEP_STATUS.FAILED, getSavingFailureMessage(error, resourceLabel, genericMessage), undefined));
        trackResourceSavingFailedEvent(resourceName, error, context);
        nextSavingHandler();
    }));
};

const setVideoDirtyState = (dispatch) => {
    dispatch(setProperty('videoDirty', true));
    setBrowserUnload(true);
};

// DUMB ACTIONS

const setProperty = (key, value) => {
    return {
        type: types.SET_PROPERTY,
        key: key,
        value: value
    }
}

const setPreviewProperty = (key, value) => {
    return {
        type: types.SET_PREVIEW_PROPERTY,
        key: key,
        value: value
    }
}

const setPreviewPropertyWithoutTracking = (key, value) => {
    return {
        type: types.SET_PREVIEW_PROPERTY_WITHOUT_TRACKING,
        key: key,
        value: value
    }
}

const updateVideoSettingsAction = (newSettings, testDirty = true) => {
    return {
        type: types.UPDATE_VIDEO_SETTINGS,
        newSettings: newSettings,
        testDirty: testDirty,
    }
}

const updateAudioSettings = (newSettings) => {
    return {
        type: types.UPDATE_AUDIO_SETTINGS,
        newSettings: newSettings
    }
}

// The reducer marks the audio as dirty by itself when a setting really changes, so an unchanged name stays clean.
const renameAudioFile = (displayFileName) => updateAudioSettings({ displayFileName });

const setVideoDirty = (isDirty) => {
    return {
        type: types.SET_VIDEO_DIRTY,
        isDirty: isDirty
    }
}

const setAudioDirty = (isDirty) => {
    return {
        type: types.SET_AUDIO_DIRTY,
        isDirty: isDirty
    }
}

const setShowAdvancedSettingsAction = (show) => {
    return {
        type: types.SET_SHOW_ADVANCED_SETTINGS,
        show: show
    }
}

const setShowUpdateDialogAction = (show) => {
    return {
        type: types.SET_SHOW_UPDATE_DIALOG,
        show: show,
    }
}

const setPreviewResolutionAction = (resolution) => {
    return {
        type: types.SET_PREVIEW_RESOLUTION,
        resolution: resolution,
    }
}

const updateLyricsSegmentAction = (index, properties) => {
    return {
        type: types.UPDATE_LYRICS_SEGMENT,
        index: index,
        properties: properties
    }
}

const removeLyricsSegmentAction = (lyricsSegment) => {
    return {
        type: types.REMOVE_LYRICS_SEGMENT,
        lyricsSegment: lyricsSegment
    }
}

const removeLyricsSegmentsAction = (lyricsSegments) => {
    return {
        type: types.REMOVE_LYRICS_SEGMENTS,
        lyricsSegments: lyricsSegments
    }
}

const setLyricsSegmentTimeAction = (segment, startTime, endTime, freezedDuration, audioDuration) => {
    return {
        type: types.SET_LYRICS_SEGMENT_TIME,
        segment: segment,
        startTime: startTime,
        endTime: endTime,
        freezedDuration: freezedDuration,
        audioDuration: audioDuration
    }
}

const addLyricsSegmentAction = (newSegment) => {
    return {
        type: types.ADD_LYRICS_SEGMENT,
        newSegment: newSegment
    }
}

const addLyricsSegmentsAction = (newSegments) => {
    return {
        type: types.ADD_LYRICS_SEGMENTS,
        newSegments: newSegments
    }
}

const undoAction = () => {
    return {
        type: types.UNDO
    }
}

const redoAction = () => {
    return {
        type: types.REDO
    }
}

const clearActionsHistory = () => {
    return {
        type: types.CLEAR_ACTIONS_HISTORY
    }
}

const updateTextElementAction = (index, properties) => {
    return {
        type: types.UPDATE_TEXT_ELEMENT,
        index: index,
        properties: properties
    }
}

const addTextElementAction = () => {
    return {
        type: types.ADD_TEXT_ELEMENT
    }
}

const removeTextElementAction = (index) => {
    return {
        type: types.REMOVE_TEXT_ELEMENT,
        index: index
    }
}

const duplicateTextElementAction = (index) => {
    return {
        type: types.DUPLICATE_TEXT_ELEMENT,
        index: index
    }
}

const updateParticleElementAction = (index, properties) => {
    return {
        type: types.UPDATE_PARTICLE_ITEM,
        index: index,
        properties: properties
    }
}

const addParticleElementAction = () => {
    return {
        type: types.ADD_PARTICLE_ITEM
    }
}

const removeParticleElementAction = (index) => {
    return {
        type: types.REMOVE_PARTICLE_ITEM,
        index: index
    }
}

const duplicateParticleElementAction = (index) => {
    return {
        type: types.DUPLICATE_PARTICLE_ITEM,
        index: index
    }
}

const updateImageElementAction = (index, properties) => {
    return {
        type: types.UPDATE_IMAGE_ELEMENT,
        index: index,
        properties: properties
    }
}

const addImageElementAction = (mediaSource, properties = {}) => {
    return {
        type: types.ADD_IMAGE_ELEMENT,
        mediaSource: mediaSource,
        properties: properties
    }
}

const removeImageElementAction = (index) => {
    return {
        type: types.REMOVE_IMAGE_ELEMENT,
        index: index
    }
}

const duplicateImageElementAction = (index) => {
    return {
        type: types.DUPLICATE_IMAGE_ELEMENT,
        index: index
    }
}

const changeImageElementOrderAction = (firstIndex, secondIndex) => {
    return {
        type: types.CHANGE_IMAGE_ELEMENT_ORDER,
        firstIndex: firstIndex,
        secondIndex: secondIndex
    }
}

const updateBackgroundSettingsAction = (properties) => {
    return {
        type: types.UPDATE_BACKGROUND_SETTINGS,
        properties: properties
    }
}

const updateLyricsStyleAction = (properties) => {
    return {
        type: types.UPDATE_LYRICS_STYLE,
        properties: properties
    }
}

const addWaveLayerAction = () => {
    return {
        type: types.ADD_WAVE_LAYER
    }
}

const updateWaveLayerAction = (index, properties) => {
    return {
        type: types.UPDATE_WAVE_LAYER,
        index: index,
        properties: properties
    }
}

const updateWaveLayerCustomOptionsAction = (index, key, value) => {
    return {
        type: types.UPDATE_WAVE_LAYER_CUSTOM_SETTINGS,
        index,
        key,
        value
    }
}

const updateWaveLayerSpinSettingsAction = (index, key, value) => {
    return {
        type: types.UPDATE_WAVE_LAYER_SPIN_SETTINGS,
        index,
        key,
        value
    }
}

const removeWaveLayerAction = (index) => {
    return {
        type: types.REMOVE_WAVE_LAYER,
        index: index
    }
}

const duplicateWaveLayerAction = (index) => {
    return {
        type: types.DUPLICATE_WAVE_LAYER,
        index: index
    }
}

const changeWaveLayerOrderAction = (firstIndex, secondIndex) => {
    return {
        type: types.CHANGE_WAVE_LAYER_ORDER,
        firstIndex: firstIndex,
        secondIndex: secondIndex
    }
}

const updateVisualizerSettingsAction = (properties) => {
    return {
        type: types.UPDATE_VISUALIZER_SETTINGS,
        properties: properties
    }
}

const updateVisualizerLayersTypeAction = (layoutType) => {
    return {
        type: types.UPDATE_VISUALIZER_LAYERS_TYPE,
        layoutType: layoutType
    }
}

const clearLyricsAction = () => {
    return {
        type: types.CLEAR_LYRICS
    }
}

const selectOrDeselectSegments = (lyricsSelectAllEnabled) => {
    return {
        type: types.SELECT_OR_DESELECT_SEGMENTS,
        lyricsSelectAllEnabled: lyricsSelectAllEnabled
    }
}

const updateElementsSettingsAction = (properties) => {
    return {
        type: types.UPDATE_ELEMENTS_SETTINGS,
        properties: properties
    }
}

const updateAspectRatioAction = (aspectRatio) => {
    return {
        type: types.UPDATE_ASPECT_RATIO,
        aspectRatio: aspectRatio
    }
}

const resetVideoSettingsAction = () => {
    return {
        type: types.RESET_VIDEO_SETTINGS
    }
}

const updateVisualizerReflectionTypeAction = (reflectionType, barCount) => {
    return {
        type: types.UPDATE_VISUALIZER_REFLECTION_TYPE,
        reflectionType,
        barCount
    }
}

// SMART OPERATIONS

const updateSavingStepStatus = (name, status, message, percentage) => {
    return (dispatch) => {
        dispatch(setProperty(`videoSavingState.${name}.status`, status));
        dispatch(setProperty(`videoSavingState.${name}.message`, message));

        if (percentage !== undefined)
            dispatch(setProperty(`videoSavingState.${name}.percentage`, percentage));

        const alias = name === 'images' ? 'imageElements' : (name === 'imageElements' ? 'images' : null);
        if (alias) {
            dispatch(setProperty(`videoSavingState.${alias}.status`, status));
            dispatch(setProperty(`videoSavingState.${alias}.message`, message));
            if (percentage !== undefined)
                dispatch(setProperty(`videoSavingState.${alias}.percentage`, percentage));
        }
    }
}

/**
 * Wait until background video elements finish loading.
 * Prevents capturing a blank thumbnail or creating an export before the background is ready.
 */
const waitForBackgroundVideosToLoad = async (getState, maxWaitMs = 5000, intervalMs = 150) => {
    const start = Date.now();
    while (getState().create.backgroundVideosLoading) {
        if (Date.now() - start > maxWaitMs) break;
        await sleep(intervalMs);
    }
}

const initializeVideoProjectProgress = () => {
    return async (dispatch, getState) => {
        const state = getState();
        const { videoSettings } = state.createPreview.present;
        const { audioDirty, unsavedAudioFile, videoDirty } = state.create;

        // Determine if the logo needs to be updated.
        const logoMediaSource = videoSettings.visualizer.mediaSource;
        const logoUpdateOperationStamp = videoSettings.visualizer.mediaSource.operationStamp;
        const isLogoChanged = logoUpdateOperationStamp !== getLastLogoUpdateStamp();
        const shouldUpdateLogo = (isLogoChanged && !logoMediaSource.isPreset);

        if (shouldUpdateLogo) {
            dispatch(updateSavingStepStatus("logo", VIDEO_SAVING_STEP_STATUS.PENDING, undefined, 0));
        }

        // Determine if the background needs to be updated.
        const backgroundMediaSource = videoSettings.background.mediaSource;
        const backgroundUpdateOperationStamp = videoSettings.background.mediaSource.operationStamp;
        const isBackgroundChanged = backgroundUpdateOperationStamp !== getLastBackgroundUpdateStamp();
        const shouldUpdateBackground = (isBackgroundChanged && !backgroundMediaSource.isPreset);

        if (shouldUpdateBackground) {
            dispatch(updateSavingStepStatus("background", VIDEO_SAVING_STEP_STATUS.PENDING, undefined, 0));
        }

        // Determine if the image elements need to be updated (uploading pictures and/or syncing with video).
        const imageElements = readImageElements(videoSettings);
        const hasImagesToUpload = getImageElementsToUpload(imageElements).length > 0;
        const hasImagesToSync = shouldUpdateImageElements(state);

        if (hasImagesToUpload || hasImagesToSync) {
            dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.PENDING, undefined, 0));
        }

        // Determine if the audio needs to be updated.
        const shouldUpdateAudio = Boolean(audioDirty) || Boolean(unsavedAudioFile);
        if (shouldUpdateAudio) {
            dispatch(updateSavingStepStatus("audio", VIDEO_SAVING_STEP_STATUS.PENDING, undefined, 0));
        }

        // Determine if the thumbnail needs to be updated.
        const isNewVideo = !videoSettings.id;
        const isThumbnailChanged = (videoDirty || isNewVideo) && Boolean(PixiRendererInstance);
        const shouldUpdateThumbnail = isThumbnailChanged || Boolean(window.lastThumbnailSavingFailed);

        if (shouldUpdateThumbnail) {
            dispatch(updateSavingStepStatus("thumbnail", VIDEO_SAVING_STEP_STATUS.PENDING, undefined, 0));
        }
    }
}

const saveEmojiImages = (nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';

        try {
            const state = getState();
            const { videoSettings } = state.createPreview.present;

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Emoji");

            // Start the saving process.
            dispatch(updateSavingStepStatus("emoji", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 10));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Emoji images saving aborted.");

            const emojiLimit = 40;
            const defaultFontSize = 42;

            failureStage = 'Emoji Extraction';

            // Create the images for each emoji in the text and lyrics.
            const textEmojiImages = await extractEmojiAndConvertToBase64(videoSettings.text.textElements, defaultFontSize);
            dispatch(updateSavingStepStatus("emoji", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 50));
            const lyricsEmojiImages = await extractEmojiAndConvertToBase64(videoSettings.lyrics.segments, videoSettings.lyrics.style.fontSize);
            const emojiImages = [...textEmojiImages, ...lyricsEmojiImages];

            // Stop the saving process if the limit is exceeded.
            if (emojiImages.length > emojiLimit) {
                const message = `Emoji limit exceeded. You can use up to ${emojiLimit} emojis. Please remove some emojis.`;
                dispatch(updateSavingStepStatus("emoji", VIDEO_SAVING_STEP_STATUS.FAILED, message, undefined));
                nextSavingHandler();
                return;
            }

            // Just save the emoji images in the object, the next step will save them to the server.
            dispatch(setPreviewPropertyWithoutTracking('videoSettings.emojiImages', emojiImages));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("emoji", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "emoji",
                resourceLabel: "emoji",
                resourceName: 'EmojiImages',
                genericMessage: "Failed to save emoji images.",
                retryAction: () => saveEmojiImages(nextSavingHandler),
                context: { failureStage: failureStage },
                nextSavingHandler: nextSavingHandler,
            });
        }
    }
}

const saveVideoSettings = (navigate, nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';

        try {
            const state = getState();
            const videoSettings = state.createPreview.present.videoSettings;
            const isPresetMode = window.location.pathname.startsWith("/create/p");
            const isNewVideo = (!videoSettings.id && window.location.pathname === '/create') || isPresetMode;

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Settings");

            // Start the saving process.
            dispatch(updateSavingStepStatus("settings", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 20));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Video settings saving aborted.");

            failureStage = 'DB Sync';

            // Update the video settings on the server.
            const response = await requestWithRetry(() => axios.post('api/media/UpdateVideoSettings', { videoSettings }, { signal: abortController.signal }));
            if (!response.data?.videoId) {
                const message = "Video identifier is missing after video saving.";
                dispatch(updateSavingStepStatus("settings", VIDEO_SAVING_STEP_STATUS.FAILED, message, undefined));
                nextSavingHandler(undefined);
                return;
            }

            dispatch(updateSavingStepStatus("settings", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 70));

            if (isNewVideo) {
                dispatch(setPreviewProperty('videoSettings.id', response.data.videoId));

                if (navigate) {
                    navigate(`/create/${response.data.videoId}`, { replace: true });
                }

                dispatch(clearActionsHistory());
                previewHistoryFilter.enableHistoryTracking();
            }

            dispatch(videoActions.setVideosFresh(false));
            dispatch(setVideoDirty(false));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("settings", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler(response.data.videoId);
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "settings",
                resourceLabel: "settings",
                resourceName: 'VideoSettings',
                genericMessage: "Failed to save video settings.",
                retryAction: () => saveVideoSettings(navigate, nextSavingHandler),
                context: { failureStage: failureStage },
                nextSavingHandler: () => nextSavingHandler(undefined),
            });
        }
    }
}

const saveLogo = (videoId, nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';
        let fileSizeMb = null;

        try {
            const state = getState();
            const videoSettings = state.createPreview.present.videoSettings;
            const logoMediaSource = videoSettings.visualizer.mediaSource;
            const logoUpdateOperationStamp = videoSettings.visualizer.mediaSource.operationStamp;
            const isLogoChanged = logoUpdateOperationStamp !== getLastLogoUpdateStamp();
            const shouldUpdateLogo = (isLogoChanged && !logoMediaSource.isPreset);
            const newLogoCriteria = { type: logoMediaSource.mediaResourceType, extension: 'png' };

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Logo");

            // Start the saving process.
            dispatch(updateSavingStepStatus("logo", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 0));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Logo saving aborted.");

            // Stop the saving process if the logo was not changed.
            if (!shouldUpdateLogo) {
                dispatch(updateSavingStepStatus("logo", VIDEO_SAVING_STEP_STATUS.UNCHANGED, undefined, 0));
                nextSavingHandler();
                return;
            }

            // Upload the new file to storage if the logo is selected from the local machine.
            const needToUploadNewFile = logoMediaSource.mediaSourceType !== MEDIA_SOURCE_TYPE.USER_ASSET;
            if (needToUploadNewFile) {
                if (logoMediaSource.directUpload?.storageName) {
                    // The file already reached storage at pick time (Direct Upload), so only
                    // the reference has to be pointed at it.
                    logoMediaSource.data = createMediaSourceDataByType(logoMediaSource.directUpload.url, logoMediaSource.mediaResourceType);
                } else {
                    failureStage = 'Get Upload URL';
                    const newFileUrl = `/api/urls/GetLogoUploadUrl?nocache=${performance.now()}`;
                    const response = await requestWithRetry(() => axios.post(newFileUrl, newLogoCriteria, { signal: abortController.signal }));
                    const newFile = response.data;

                    failureStage = 'Blob Creation';
                    const blob = await createBlobFromUrl(getMediaSourceSingleUrl(logoMediaSource), abortController);
                    fileSizeMb = Number(convertToMegabytes(blob.size).toFixed(2));

                    failureStage = 'Azure Upload';
                    const onProgress = (percentage) => dispatch(updateSavingStepStatus("logo", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, percentage));
                    await uploadFileToBlobStorage(blob, newFile.url, onProgress);
                    logoMediaSource.data = createMediaSourceDataByType(newFile.url, logoMediaSource.mediaResourceType);
                }
            }

            failureStage = 'DB Sync';

            // Update the logo on the server.
            const payload = { videoId: videoId, mediaSource: logoMediaSource };
            await requestWithRetry(() => axios.post(`/api/media/UpdateLogo`, payload, { signal: abortController.signal }));
            saveLastLogoUpdateStamp(logoUpdateOperationStamp);
            dispatch(setPreviewPropertyWithoutTracking('videoSettings.visualizer.mediaSource.data', logoMediaSource.data));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("logo", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "logo",
                resourceLabel: "logo",
                resourceName: 'Logo',
                genericMessage: "Failed to save logo.",
                retryAction: () => saveLogo(videoId, nextSavingHandler),
                context: { failureStage: failureStage, videoId: videoId ?? 'unknown', sizeMb: fileSizeMb },
                nextSavingHandler: nextSavingHandler,
            });
        }
    }
}

const saveBackground = (videoId, nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';
        let fileSizeMb = null;

        try {
            const state = getState();
            const videoSettings = state.createPreview.present.videoSettings;
            const backgroundMediaSource = videoSettings.background.mediaSource;
            const backgroundUpdateOperationStamp = videoSettings.background.mediaSource.operationStamp;
            const mediaSourceTypesToUpdateWithoutUpload = new Set([MEDIA_SOURCE_TYPE.STOCK_VIDEO, MEDIA_SOURCE_TYPE.USER_ASSET, MEDIA_SOURCE_TYPE.VIMEO]);
            const isBackgroundChanged = backgroundUpdateOperationStamp !== getLastBackgroundUpdateStamp();
            const shouldUpdateBackground = (isBackgroundChanged && !backgroundMediaSource.isPreset);
            const backgroundExtension = backgroundMediaSource.mediaResourceType == MEDIA_RESOURCE_TYPE.IMAGE ? 'jpeg' : 'mp4';
            const newBackgroundCriteria = { type: backgroundMediaSource.mediaResourceType, extension: backgroundExtension };

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Background");

            // Start the saving process.
            dispatch(updateSavingStepStatus("background", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 0));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Background saving aborted.");

            // Stop the saving process if the background was not changed.
            if (!shouldUpdateBackground) {
                dispatch(updateSavingStepStatus("background", VIDEO_SAVING_STEP_STATUS.UNCHANGED, undefined, 0));
                nextSavingHandler();
                return;
            }

            // Upload the new file to storage if the background is selected from the local machine.
            // A color or gradient has no file, but the server is still told about it below, so it lets
            // go of the media background the video used before, which a reload would otherwise restore.
            const shouldUploadBackgroundFile = !mediaSourceTypesToUpdateWithoutUpload.has(backgroundMediaSource.mediaSourceType)
                && !isColorOrGradientSource(backgroundMediaSource);

            if (shouldUploadBackgroundFile) {
                if (backgroundMediaSource.directUpload?.storageName) {
                    // The file already reached storage at pick time (Direct Upload), so only
                    // the reference has to be pointed at it.
                    backgroundMediaSource.data = createMediaSourceDataByType(backgroundMediaSource.directUpload.url, backgroundMediaSource.mediaResourceType);
                } else {
                    failureStage = 'Get Upload URL';
                    const newFileUrl = `/api/urls/GetBackgroundUploadUrl?nocache=${performance.now()}`;
                    const response = await requestWithRetry(() => axios.post(newFileUrl, newBackgroundCriteria, { signal: abortController.signal }));
                    const newFile = response.data;

                    failureStage = 'Blob Creation';
                    const blob = await createBlobFromUrl(getMediaSourceSingleUrl(backgroundMediaSource), abortController);
                    fileSizeMb = Number(convertToMegabytes(blob.size).toFixed(2));

                    failureStage = 'Azure Upload';
                    const onProgress = (percentage) => dispatch(updateSavingStepStatus("background", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, percentage));
                    await uploadFileToBlobStorage(blob, newFile.url, onProgress);
                    backgroundMediaSource.data = createMediaSourceDataByType(newFile.url, backgroundMediaSource.mediaResourceType);
                }
            }

            failureStage = 'DB Sync';

            // Update the background on the server.
            const payload = { videoId: videoId, mediaSource: backgroundMediaSource };
            await requestWithRetry(() => axios.post(`/api/media/UpdateBackground`, payload, { signal: abortController.signal }));
            saveLastBackgroundUpdateStamp(backgroundUpdateOperationStamp);
            dispatch(setPreviewPropertyWithoutTracking('videoSettings.background.mediaSource.data', backgroundMediaSource.data));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("background", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "background",
                resourceLabel: "background",
                resourceName: 'Background',
                genericMessage: "Failed to save background.",
                retryAction: () => saveBackground(videoId, nextSavingHandler),
                context: { failureStage: failureStage, videoId: videoId ?? 'unknown', sizeMb: fileSizeMb },
                nextSavingHandler: nextSavingHandler,
            });
        }
    }
}

// The reducer keeps the legacy `images` key in lockstep with `imageElements`, but only `imageElements`
// is stored by the server, so that is the key read first.
const readImageElements = (videoSettings) =>
    videoSettings.elements.imageElements || videoSettings.elements.images || [];

// A picture is ready for storage once it lives in our own blob container. Anything else, a stock
// photo still hosted by Pexels or a file the browser only holds in memory, has to be uploaded first.
// An element the server could not resolve carries no picture at all, and reading a blob out of an
// empty URL would upload the page the browser is on instead.
const getImageElementsToUpload = (imageElements) => {
    return imageElements.filter((imageElement) =>
        imageElement.mediaSource?.mediaSourceType !== MEDIA_SOURCE_TYPE.USER_ASSET
        && Boolean(getMediaSourceSingleUrl(imageElement.mediaSource)));
}

// The pictures are worth a round trip only when they were touched, which also covers the case where
// the last one was removed and the asset it used has to be let go of. A video that carries a picture
// the server could not resolve is taken along as well: that one has no asset row, because a save that
// stored the settings but never reached this step left it behind, and nothing else would notice.
const shouldUpdateImageElements = (state) => {
    const imageElements = readImageElements(state.createPreview.present.videoSettings);

    return state.create.imageElementsChanged
        || imageElements.some((imageElement) => !imageElement.mediaSource);
}

const getImageElementFileExtension = (blob) => {
    switch (blob?.type) {
        case 'image/jpeg': return 'jpeg';
        case 'image/webp': return 'webp';
        case 'image/gif': return 'gif';
        default: return 'png';
    }
}

const applyImageElementMediaSources = (mediaSourceByElementId) => {
    return (dispatch, getState) => {
        const imageElements = readImageElements(getState().createPreview.present.videoSettings);

        const updatedImageElements = imageElements.map((imageElement) => {
            const mediaSource = mediaSourceByElementId[imageElement.id];
            return mediaSource ? { ...imageElement, mediaSource } : imageElement;
        });

        // Both keys, exactly as the reducer writes them: updating only `images` left `imageElements`,
        // the key the settings are stored from, pointing at the picture before its upload.
        dispatch(setPreviewPropertyWithoutTracking('videoSettings.elements.imageElements', updatedImageElements));
        dispatch(setPreviewPropertyWithoutTracking('videoSettings.elements.images', updatedImageElements));
    }
}

/**
 * Saves image elements in a single sequential step: uploads any new pictures to storage,
 * persists the updated video settings, and links the assets to the video.
 */
const saveImageElements = (videoId, nextSavingHandler) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const imageElements = readImageElements(state.createPreview.present.videoSettings);
            const changedElements = getImageElementsToUpload(imageElements);
            const needsSync = shouldUpdateImageElements(state);

            // Pictures that already reached storage at pick time (Direct Upload) only need
            // their references pointed at the stored files; the rest still upload here.
            const directUploadedElements = changedElements.filter((imageElement) => imageElement.mediaSource.directUpload?.storageName);
            const elementsToUpload = changedElements.filter((imageElement) => !imageElement.mediaSource.directUpload?.storageName);

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Images");

            // Stop the saving process if this video has nothing to do with images.
            if (changedElements.length === 0 && !needsSync) {
                dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.UNCHANGED, undefined, 0));
                nextSavingHandler();
                return;
            }

            dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 0));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Image elements saving aborted.");

            // 1. Upload any local / external files to storage. Pictures that already reached
            //    storage at pick time (Direct Upload) skip the upload and only get their
            //    references pointed at the stored files.
            const uploadedMediaSources = {};

            directUploadedElements.forEach((imageElement) => {
                uploadedMediaSources[imageElement.id] = {
                    ...imageElement.mediaSource,
                    data: createMediaSourceDataByType(imageElement.mediaSource.directUpload.url, MEDIA_RESOURCE_TYPE.IMAGE)
                };
            });

            if (elementsToUpload.length > 0) {
                const blobs = await Promise.all(elementsToUpload.map((imageElement) => createBlobFromUrl(getMediaSourceSingleUrl(imageElement.mediaSource), abortController)));

                const uploadCriteria = blobs.map((blob) => ({
                    type: MEDIA_RESOURCE_TYPE.IMAGE,
                    extension: getImageElementFileExtension(blob)
                }));

                const newFilesUrl = `/api/urls/GetImageElementUploadUrls?nocache=${performance.now()}`;
                const response = await requestWithRetry(() => axios.post(newFilesUrl, uploadCriteria, { signal: abortController.signal }));
                const newFiles = response.data;

                for (let uploadIndex = 0; uploadIndex < elementsToUpload.length; uploadIndex++) {
                    const imageElement = elementsToUpload[uploadIndex];
                    const blob = blobs[uploadIndex];
                    const newFile = newFiles[uploadIndex];

                    // The upload takes the first 60% of the step, the rest is left for storing and linking.
                    const onProgress = (percentage) => {
                        const uploadedShare = (uploadIndex + percentage / 100) / elementsToUpload.length;
                        dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, Math.round(uploadedShare * 60)));
                    };

                    await uploadFileToBlobStorage(blob, newFile.url, onProgress);

                    uploadedMediaSources[imageElement.id] = {
                        ...imageElement.mediaSource,
                        data: createMediaSourceDataByType(newFile.url, MEDIA_RESOURCE_TYPE.IMAGE)
                    };
                }
            }

            if (changedElements.length > 0)
                dispatch(applyImageElementMediaSources(uploadedMediaSources));

            if (elementsToUpload.length > 0) {
                // The settings were stored earlier in the chain, while these pictures had no blob yet, so they
                // are stored again: the server takes the blob name out of the new URLs, and UpdateImageElements
                // below links the pictures by reading those names from the stored document. Direct-uploaded
                // pictures carried their blob names already when the settings were stored, so they alone
                // do not force this second store.
                const updatedVideoSettings = getState().createPreview.present.videoSettings;
                await requestWithRetry(() => axios.post('api/media/UpdateVideoSettings', { videoSettings: updatedVideoSettings }, { signal: abortController.signal }));
            }

            // 2. Link the uploaded pictures to the video.
            dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 75));

            const response = await requestWithRetry(() => axios.post(`/api/media/UpdateImageElements`, { videoId }, { signal: abortController.signal }));

            // The answer carries the stored media sources, so the pictures stop looking like files
            // that still need uploading and the next save leaves them alone.
            const storedMediaSources = {};
            (response.data.imageElements || []).forEach((imageElement) => {
                if (imageElement.mediaSource)
                    storedMediaSources[imageElement.id] = imageElement.mediaSource;
            });

            dispatch(applyImageElementMediaSources(storedMediaSources));
            dispatch(setProperty('imageElementsChanged', false));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            console.error(error);
            if (axios.isCancel(error) || error instanceof SavingAbortedError) {
                dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.CANCELLED, "", undefined));
                nextSavingHandler();
                return;
            }
            dispatch(appOperations.failRequest(error, () => saveImageElements(videoId, nextSavingHandler), () => {
                dispatch(updateSavingStepStatus("images", VIDEO_SAVING_STEP_STATUS.FAILED, getSavingFailureMessage(error, "images", "Failed to save images."), undefined));
                trackResourceSavingFailedEvent('ImageElements', error);
                nextSavingHandler();
            }));
        }
    }
}

const saveThumbnail = (videoId, isVideoDirty, isNewVideo, nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';
        let fileSizeMb = null;

        try {
            const state = getState();
            const videoSettings = state.createPreview.present.videoSettings;
            const aspectRatio = videoSettings.aspectRatio;
            const newThumbnailCriteria = { type: MEDIA_RESOURCE_TYPE.IMAGE, extension: 'webp' };
            const isThumbnailChanged = (isVideoDirty || isNewVideo) && Boolean(PixiRendererInstance);
            const shouldUpdateThumbnail = isThumbnailChanged || Boolean(window.lastThumbnailSavingFailed);

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Thumbnail");

            // Start the saving process.
            dispatch(updateSavingStepStatus("thumbnail", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 0));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Thumbnail saving aborted.");

            // Stop the saving process if the thumbnail was not changed.
            if (!shouldUpdateThumbnail) {
                dispatch(updateSavingStepStatus("thumbnail", VIDEO_SAVING_STEP_STATUS.UNCHANGED, undefined, 0));
                nextSavingHandler();
                return;
            }

            failureStage = 'Get Upload URL';
            const newFileUrl = `/api/urls/GetThumbnailUploadUrl?nocache=${performance.now()}`;
            const response = await requestWithRetry(() => axios.post(newFileUrl, newThumbnailCriteria, { signal: abortController.signal }));
            const newFile = response.data;

            failureStage = 'Blob Creation';

            // Generate the thumbnail from current preview.
            const canvas = PixiRendererInstance.getExtractedCanvas();
            const snapshotBlobFile = await getThumbnailSnapshot(canvas, aspectRatio);
            fileSizeMb = Number(convertToMegabytes(snapshotBlobFile.size).toFixed(2));

            failureStage = 'Azure Upload';

            // Upload the new file to storage.
            const onProgress = (percentage) => dispatch(updateSavingStepStatus("thumbnail", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, percentage));
            await uploadFileToBlobStorage(snapshotBlobFile, newFile.url, onProgress);

            failureStage = 'DB Sync';

            // Update the thumbnail on the server.
            const payload = { videoId: videoId, thumbnailUrl: newFile.url };
            await requestWithRetry(() => axios.post(`/api/media/UpdateThumbnail`, payload, { signal: abortController.signal }));

            window.lastThumbnailSavingFailed = false;
            dispatch(setPreviewPropertyWithoutTracking('videoSettings.thumbnailUrl', newFile.url));
            dispatch(videoActions.setVideosFresh(false));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("thumbnail", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "thumbnail",
                resourceLabel: "thumbnail",
                resourceName: 'Thumbnail',
                genericMessage: "Failed to save thumbnail.",
                retryAction: () => saveThumbnail(videoId, isVideoDirty, isNewVideo, nextSavingHandler),
                onFailed: () => { window.lastThumbnailSavingFailed = true; },
                context: { failureStage: failureStage, videoId: videoId ?? 'unknown', sizeMb: fileSizeMb },
                nextSavingHandler: nextSavingHandler,
            });
        }
    }
}

const saveAudio = (videoId, nextSavingHandler) => {
    return async (dispatch, getState) => {
        let failureStage = 'Preparation';
        let fileSizeMb = null;

        try {
            const state = getState();
            const { audioSettings, audioDirty, unsavedAudioFile } = state.create;
            const shouldUpdateAudio = Boolean(audioDirty) || Boolean(unsavedAudioFile);
            let storageFileName = undefined;

            // Warning: This function is used for QA purposes only.
            checkAndTriggerVideoSettingsFailure("Audio");

            // Start the saving process.
            dispatch(updateSavingStepStatus("audio", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, 0));

            // Stop the saving process if the user navigates away.
            const abortController = window.createPageAbortController;
            if (abortController.signal.aborted)
                throw new SavingAbortedError("Audio saving aborted.");

            // Stop the saving process if the audio settings were not changed.
            if (!shouldUpdateAudio) {
                dispatch(updateSavingStepStatus("audio", VIDEO_SAVING_STEP_STATUS.UNCHANGED, undefined, 0));
                nextSavingHandler();
                return;
            }

            // If there is an unsaved audio file, upload it to storage.
            if (unsavedAudioFile) {
                if (unsavedAudioFile.storageName) {
                    // The file was already uploaded to storage at pick time
                    // (Optimization Mode) — only the database sync is needed.
                    storageFileName = unsavedAudioFile.storageName;
                } else {
                    failureStage = 'Get Upload URL';
                    const newAudioCriteria = { extension: unsavedAudioFile.extension };
                    const newFileUrl = `/api/urls/GetAudioUploadUrl?nocache=${performance.now()}`;
                    const response = await requestWithRetry(() => axios.post(newFileUrl, newAudioCriteria, { signal: abortController.signal }));
                    const newFile = response.data;

                    failureStage = 'Blob Creation';
                    const blob = await createBlobFromUrl(audioSettings.url, abortController);
                    fileSizeMb = Number(convertToMegabytes(blob.size).toFixed(2));

                    failureStage = 'Azure Upload';
                    const onProgress = (percentage) => dispatch(updateSavingStepStatus("audio", VIDEO_SAVING_STEP_STATUS.IN_PROGRESS, undefined, percentage));
                    await uploadFileToBlobStorage(blob, newFile.url, onProgress);
                    storageFileName = newFile.name;
                }
            }

            failureStage = 'DB Sync';

            // Update the audio settings in database.
            const settingsPayload = {
                displayName: audioSettings.displayFileName,
                duration: audioSettings.duration,
                bassMaxMagnitude: audioSettings.bassMaxMagnitude,
                wideMaxMagnitude: audioSettings.wideMaxMagnitude,
                startTime: audioSettings.startTime,
                endTime: audioSettings.endTime,
            };
            const filePayload = unsavedAudioFile && storageFileName ? { ...unsavedAudioFile, storageName: storageFileName } : null;
            await requestWithRetry(() => axios.post(`/api/media/UpdateVideoAudio`, { videoId: videoId, audioSettings: settingsPayload, unsavedAudioFile: filePayload }, { signal: abortController.signal }));

            dispatch(setAudioDirty(false));
            dispatch(setProperty('unsavedAudioFile', undefined));

            // The audio name is what My Videos shows for the project, and it can change without a new file.
            dispatch(videoActions.setVideosFresh(false));

            // Call the next saving action.
            dispatch(updateSavingStepStatus("audio", VIDEO_SAVING_STEP_STATUS.COMPLETED, "", 100));
            nextSavingHandler();
        } catch (error) {
            handleResourceSavingError(dispatch, error, {
                stepName: "audio",
                resourceLabel: "audio",
                resourceName: 'Audio',
                genericMessage: "Failed to save audio.",
                retryAction: () => saveAudio(videoId, nextSavingHandler),
                context: { failureStage: failureStage, videoId: videoId ?? 'unknown', sizeMb: fileSizeMb },
                nextSavingHandler: nextSavingHandler,
            });
        }
    }
}

// Records the outcome of a finished save attempt, exactly once per attempt: a run with
// any failed step bumps the consecutive-failure counter (several failed steps in one
// run still count as one attempt), a run without failures resets it.
const finalizeSaveAttemptOutcome = () => {
    return (dispatch, getState) => {
        const { videoSavingState, consecutiveSaveFailures } = getState().create;

        const hasAnyFailure = Object.values(videoSavingState).some(
            (step) => step?.status === VIDEO_SAVING_STEP_STATUS.FAILED);

        if (hasAnyFailure)
            dispatch(setProperty('consecutiveSaveFailures', consecutiveSaveFailures + 1));
        else
            dispatch(setProperty('consecutiveSaveFailures', 0));
    }
}

const saveVideoProject = (onComplete, showSavingPanel, navigate) => {
    return async (dispatch, getState) => {
        const state = getState();
        const videoSettings = state.createPreview.present.videoSettings;

        // The properties to check whether we need to save new thumbnail.
        // We save them here because some saving methods can change the state before we start to save the thumbnail.
        const isVideoDirty = state.create.videoDirty;
        const isNewVideo = !videoSettings.id;

        try {
            const videoSavingState = { ...DEFAULT_VIDEO_SAVING_STATE };
            videoSavingState.savingInProgress = true;
            videoSavingState.savingPanelOpen = showSavingPanel;
            dispatch(setProperty('videoSavingState', videoSavingState));

            await dispatch(initializeVideoProjectProgress());

            // 1. Save the emoji images.
            dispatch(saveEmojiImages(() => {
                // 2. Save the video settings.
                dispatch(saveVideoSettings(navigate, (videoId) => {
                    // We can't continue if the video id is missing.
                    if (videoId === undefined) {
                        dispatch(setProperty('videoSavingState.savingInProgress', false));
                        dispatch(finalizeSaveAttemptOutcome());
                        return;
                    }

                    // 3. Save the audio.
                    dispatch(saveAudio(videoId, () => {
                        // 4. Save the background.
                        dispatch(saveBackground(videoId, () => {
                            // 5. Save the logo.
                            dispatch(saveLogo(videoId, () => {
                                // 6. Save the image elements (uploads pictures and links assets sequentially in this single step).
                                dispatch(saveImageElements(videoId, () => {
                                    // 7. Save the thumbnail.
                                    dispatch(saveThumbnail(videoId, isVideoDirty, isNewVideo, () => {
                                        dispatch(setProperty('videoSavingState.savingInProgress', false));
                                        dispatch(finalizeSaveAttemptOutcome());
                                        onComplete?.(videoId);
                                    }));
                                }));
                            }));
                        }));
                    }));
                }));
            }));
        } catch (error) {
            console.error(error);
            dispatch(setProperty('videoSavingState.savingInProgress', false));
            trackResourceSavingFailedEvent('VideoProject', error);
        }
    }
}

const loadBackgroundDimensions = (mediaSource) => {
    return (dispatch) => {
        try {
            const type = mediaSource?.mediaResourceType;
            const url = getMediaSourceSingleUrl(mediaSource);
            if (!url) {
                dispatch(setProperty('backgroundDimensions', undefined));
                return;
            }

            const abortController = window.createPageAbortController;
            getMediaDimensions(url, type, abortController?.signal)
                .then(dim => {
                    console.log('Background dimensions loaded:', dim);
                    if (abortController?.signal?.aborted) return;
                    if (dim?.width && dim?.height)
                        dispatch(setProperty('backgroundDimensions', { width: dim.width, height: dim.height }));
                    else dispatch(setProperty('backgroundDimensions', undefined));
                })
                .catch(() => {
                    if (!abortController?.signal?.aborted) dispatch(setProperty('backgroundDimensions', undefined));
                });
        } catch (_) {
            dispatch(setProperty('backgroundDimensions', undefined));
        }
    }
}

const getVideoSettings = (videoId, callback) => {
    return (dispatch, getState) => {
        const state = getState();
        dispatch(setPreviewProperty('lyricsSelectAllEnabled', false));
        const previewAudioAnalysisDisabled = state.create.previewAudioAnalysisDisabled || state.create.optimizationModeEnabled;

        if (!videoId) {
            dispatch(applySettingsForEmptyVideoProject());
            return;
        }

        // Disable the history tracking to skip all the initial actions
        previewHistoryFilter.disableHistoryTracking();

        dispatch(setProperty('presetDialogOpen', false));
        dispatch(setProperty('videoSettingsLoading', true));

        const abortController = window.createPageAbortController;

        api.get('api/media/GetVideoSettings/' + videoId, { signal: abortController.signal }).then(response => {
            response.data.videoSettings.visualizer.mediaSource.operationStamp = `${randomString()}(INITIAL)`;
            response.data.videoSettings.background.mediaSource.operationStamp = `${randomString()}(INITIAL)`;

            response.data.videoSettings.visualizer.waveCircles.forEach((layer) => {
                layer.id = generateGuid();
                layer.operationStamp = randomString();
            });

            // Handle background media source dimensions asynchronously (non-blocking)
            dispatch(loadBackgroundDimensions(response.data.videoSettings.background.mediaSource));

            saveLastLogoUpdateStamp(response.data.videoSettings.visualizer.mediaSource.operationStamp);
            saveLastBackgroundUpdateStamp(response.data.videoSettings.background.mediaSource.operationStamp);
            dispatch(setProperty('unsavedAudioFile', undefined));

            const isVideoMediaResourceType = response.data.videoSettings.background.mediaSource.mediaResourceType == MEDIA_RESOURCE_TYPE.VIDEO;
            if (isVideoMediaResourceType)
                dispatch(setProperty('backgroundVideosLoading', true));

            createLocalMediaSourceUrls(response.data.videoSettings.background.mediaSource, (localMediaSource) => {
                response.data.videoSettings.background.mediaSource = localMediaSource;
                dispatch(updateVideoSettings(response.data.videoSettings));
                dispatch(setProperty('videoSettingsLoading', false));
                dispatch(setVideoDirty(false));

                // Clear all the previous history items and enable tracking for user actions
                dispatch(clearActionsHistory());
                previewHistoryFilter.enableHistoryTracking();
            });

            const audioSettings = { ...response.data.audioSettings };
            if (Boolean(response.data.audioSettings.url)) {
                if ((!audioSettings.bassMaxMagnitude || !audioSettings.wideMaxMagnitude) && !previewAudioAnalysisDisabled) {
                    // Start the analysis automatically when magnitudes values are missing and the audio file is small.
                    if (audioSettings.duration && audioSettings.duration <= CreatePageConstants.AUTOMATED_ANALYSIS_AUDIO_DURATION_LIMIT)
                        dispatch(analyzeAudioMaxMagnitudes(response.data.audioSettings.url));
                }
            } else {
                audioSettings.url = DEFAULT_AUDIO_TRACK.SOUND_URL;
            }

            dispatch(updateAudioSettings(audioSettings));
            dispatch(setAudioDirty(false));

            dispatch(exportsActions.loadVideoExports(videoId));
            dispatch(validateAndApplyAudioDuration(videoId, response.data.audioSettings));
        }).catch((error) => {
            if (error?.code == "ERR_CANCELED")
                return;

            dispatch(appOperations.failRequest(error, () => getVideoSettings(videoId), 'Video not found.'));
        }).finally(() => {
            dispatch(setProperty('videoSettingsLoading', false));
            if (typeof callback === "function") {
                callback();
            }
        });
    }
}

const validateAndApplyAudioDuration = (videoId, audioSettings) => {
    return async (dispatch) => {
        const audioDuration = audioSettings.duration;
        const audioUrl = audioSettings.url;

        const correctAudioTimeRangeOnServer = async (startTime, endTime) => {
            try {
                const payload = { videoId, startTime, endTime };
                await axios.post(`/api/media/CorrectAudioTimeRange`, payload);
            } catch (error) {
                console.error(error);
            }
        };

        if (audioDuration && audioUrl) {
            const roundedDuration = Math.floor(audioDuration);
            dispatch(setProperty('audioDuration', roundedDuration));

            if (audioSettings.endTime > roundedDuration) {
                dispatch(updateAudioSettings({ startTime: 0, endTime: roundedDuration }));
                await correctAudioTimeRangeOnServer(0, roundedDuration);
            }
        }
        else if (audioUrl) {
            dispatch(setProperty('audioPreparing', true));
            const fileExtension = audioUrl.split('.').pop().split('?')[0];
            const extractedAudioDuration = await extractAudioDuration(fileExtension, audioUrl);
            const extractedRoundedDuration = Math.floor(extractedAudioDuration);
            dispatch(setProperty('audioDuration', extractedRoundedDuration));

            if (audioSettings.endTime > extractedRoundedDuration) {
                dispatch(updateAudioSettings({ startTime: 0, endTime: extractedRoundedDuration }));
                await correctAudioTimeRangeOnServer(0, extractedRoundedDuration);
            }
            dispatch(setProperty('audioPreparing', false));
        }
        else {
            dispatch(setProperty('audioDuration', DEFAULT_AUDIO_TRACK.DURATION));
        }
    };
}

const updateVisualizerReflectionType = (value) => {
    return (dispatch, getState) => {
        const state = getState();
        const barCountStep = BAR_COUNT_STEP[value] || 1;
        const maxBarCount = state.create.maxValues.maxBarCount;
        let barCount = state.createPreview.present.videoSettings.visualizer.barCount;

        if (barCount > maxBarCount / 2) {
            barCount -= barCount % barCountStep;
            if (value === REFLECTION_TYPE.COMBO || value === REFLECTION_TYPE.ONE_SIDE)
                barCount -= 1;
        }
        else {
            barCount += barCount % barCountStep;
            if (value === REFLECTION_TYPE.COMBO || value === REFLECTION_TYPE.ONE_SIDE)
                barCount += 1;
        }

        dispatch(updateVisualizerReflectionTypeAction(value, barCount))
    }
}

const applyAudioSettingsForPresetMode = () => {
    return (dispatch) => {
        previewHistoryFilter.disableHistoryTracking();

        let audioSettings = createObjectCopy(CreatePageConstants.DEFAULT_AUDIO_SETTINGS);
        audioSettings.url = DEFAULT_AUDIO_TRACK.SOUND_URL;

        dispatch(updateAudioSettings(audioSettings));
        dispatch(setAudioDirty(false));

        dispatch(setProperty('audioDuration', DEFAULT_AUDIO_TRACK.DURATION));
        dispatch(exportsActions.setProperty('videoExports', []));
        dispatch(clearActionsHistory());

        previewHistoryFilter.enableHistoryTracking();
    }
}

const applySettingsForEmptyVideoProject = () => {
    return (dispatch) => {
        // Disable the history tracking to skip all the initial actions
        previewHistoryFilter.disableHistoryTracking();

        dispatch(updateVideoSettings(CreatePageConstants.DEFAULT_VIDEO_SETTINGS));
        dispatch(setVideoDirty(false));

        let audioSettings = createObjectCopy(CreatePageConstants.DEFAULT_AUDIO_SETTINGS);
        audioSettings.url = DEFAULT_AUDIO_TRACK.SOUND_URL;

        dispatch(updateAudioSettings(audioSettings));
        dispatch(setAudioDirty(false));

        saveLastLogoUpdateStamp(CreatePageConstants.DEFAULT_VIDEO_SETTINGS.visualizer.mediaSource.operationStamp);
        saveLastBackgroundUpdateStamp(CreatePageConstants.DEFAULT_VIDEO_SETTINGS.background.mediaSource.operationStamp);

        dispatch(setProperty('audioDuration', DEFAULT_AUDIO_TRACK.DURATION));
        dispatch(exportsActions.setProperty('videoExports', []));

        // Clear all the previous history items and enable tracking for user actions
        dispatch(clearActionsHistory());
        previewHistoryFilter.enableHistoryTracking();
    };
}

// Uploads a locally picked media file to blob storage right away, using the same stable
// SAS-URL + chunked + retry pipeline as the save flow. Returns the uploaded file's url
// and storage name.
const uploadMediaFileToStorage = async (urlsEndpoint, criteria, file, options = {}) => {
    const { abortSignal = window.createPageAbortController.signal, onProgress } = options;

    const newFileUrl = `/api/urls/${urlsEndpoint}?nocache=${performance.now()}`;
    const response = await requestWithRetry(() => axios.post(newFileUrl, criteria, { signal: abortSignal }));

    // The image elements endpoint takes and returns a batch, so a single file is a batch of one.
    const newFile = Array.isArray(response.data) ? response.data[0] : response.data;

    await uploadFileToBlobStorage(file, newFile.url, onProgress, abortSignal);

    return { url: newFile.url, storageName: newFile.name };
};

// With Direct Upload a cropped logo or background picture goes to storage the moment the
// crop is confirmed, and the preview then uses the stored url right away instead of keeping
// the picture in browser memory - the same upload-and-switch approach as applyNewAudioFile.
// Returns the uploaded file, or null with Lazy Upload or when the upload failed; the caller
// then falls back to a local object url and the save flow uploads the file as usual.
const uploadCroppedImageDirectly = (imageBlob, mediaUsageType) => {
    return async (dispatch, getState) => {
        if (getState().create.assetUploadingMode !== ASSET_UPLOADING_MODE.DIRECT)
            return null;

        const isLogo = mediaUsageType === MEDIA_USAGE_TYPE.LOGO;

        try {
            // The extension matches the one the deferred save-time upload would request.
            const urlsEndpoint = isLogo ? 'GetLogoUploadUrl' : 'GetBackgroundUploadUrl';
            const criteria = { type: MEDIA_RESOURCE_TYPE.IMAGE, extension: isLogo ? 'png' : 'jpeg' };

            return await uploadMediaFileToStorage(urlsEndpoint, criteria, imageBlob);
        } catch (uploadError) {
            console.error(uploadError);
            dispatch(appOperations.openSnack(
                `${isLogo ? 'Logo' : 'Background'} upload failed. Uploading will be tried again when you save.`,
                ColoredSnackbarConstants.SNACKBAR_WARNING
            ));
            return null;
        }
    }
}

// The background video counterpart of uploadCroppedImageDirectly: the raw file goes to storage
// at pick time and the preview plays it from the stored url.
const uploadBackgroundVideoDirectly  = (file) => {
    return async (dispatch, getState) => {
        if (getState().create.assetUploadingMode !== ASSET_UPLOADING_MODE.DIRECT)
            return { uploadedFile: null, cancelled: false };

        const abortController = new AbortController();
        directUploadTracker.replaceBackgroundUpload(abortController);

        // Mounts the preview display loader overlay so the upload percentage has
        // somewhere to render (the picker is already closed at this point).
        dispatch(setProperty('backgroundUploading', true));

        // Leaving the page cancels the direct upload together with everything else.
        const pageAbortSignal = window.createPageAbortController.signal;
        const abortWithPage = () => abortController.abort();
        pageAbortSignal.addEventListener('abort', abortWithPage, { once: true });

        try {
            const criteria = { type: MEDIA_RESOURCE_TYPE.VIDEO, extension: 'mp4' };

            let uploadedFile;
            try {
                const onProgress = (percentage) => updateDisplayLoaderPercentage(Math.round(percentage));
                uploadedFile = await uploadMediaFileToStorage('GetBackgroundUploadUrl', criteria, file, { abortSignal: abortController.signal, onProgress });
            } finally {
                updateDisplayLoaderPercentage(undefined);
            }

            if (abortController.signal.aborted)
                return { uploadedFile: null, cancelled: true };

            return { uploadedFile, cancelled: false };
        } catch (uploadError) {
            if (axios.isCancel(uploadError) || abortController.signal.aborted)
                return { uploadedFile: null, cancelled: true };

            console.error(uploadError);
            dispatch(appOperations.openSnack(
                'Background upload failed. Uploading will be tried again when you save.',
                ColoredSnackbarConstants.SNACKBAR_WARNING
            ));
            return { uploadedFile: null, cancelled: false };
        } finally {
            pageAbortSignal.removeEventListener('abort', abortWithPage);
            // A superseded upload must not hide the overlay of the newer one still in flight.
            if (directUploadTracker.finishBackgroundUpload(abortController))
                dispatch(setProperty('backgroundUploading', false));
        }
    }
}

// The image element counterpart of uploadCroppedImageDirectly: returns the uploaded file, or
// null with Lazy Upload or when the upload failed (the caller falls back to a local url).
const uploadImageElementDirectly = (imageBlob) => {
    return async (dispatch, getState) => {
        if (getState().create.assetUploadingMode !== ASSET_UPLOADING_MODE.DIRECT)
            return null;

        // Image elements are additive, so several picks can upload at once (the picker
        // closes as soon as a file is picked). The shared loader flag and percentage
        // only clear when the last in-flight upload finishes.
        directUploadTracker.beginImageUpload();

        // Mounts the preview display loader overlay so the upload percentage has
        // somewhere to render (the picker is already closed at this point).
        dispatch(setProperty('imageElementUploading', true));

        try {
            const criteria = [{ type: MEDIA_RESOURCE_TYPE.IMAGE, extension: getImageElementFileExtension(imageBlob) }];
            const onProgress = (percentage) => updateDisplayLoaderPercentage(Math.round(percentage));
            return await uploadMediaFileToStorage('GetImageElementUploadUrls', criteria, imageBlob, { onProgress });
        } catch (uploadError) {
            console.error(uploadError);
            dispatch(appOperations.openSnack(
                'Image upload failed. Uploading will be tried again when you save.',
                ColoredSnackbarConstants.SNACKBAR_WARNING
            ));
            return null;
        } finally {
            if (directUploadTracker.finishImageUpload()) {
                updateDisplayLoaderPercentage(undefined);
                dispatch(setProperty('imageElementUploading', false));
            }
        }
    }
}

// Uploads an audio file to blob storage right away, using the same stable
// SAS-URL + chunked + retry pipeline as the save flow. Returns the uploaded
// file's url and storage name.
const uploadAudioFileToStorage = async (file, extension) => {
    const abortController = window.createPageAbortController;

    const newFileUrl = `/api/urls/GetAudioUploadUrl?nocache=${performance.now()}`;
    const response = await requestWithRetry(() => axios.post(newFileUrl, { extension: extension }, { signal: abortController.signal }));
    const newFile = response.data;

    try {
        const onProgress = (percentage) => updateDisplayLoaderPercentage(Math.round(percentage));
        await uploadFileToBlobStorage(file, newFile.url, onProgress);
    } finally {
        updateDisplayLoaderPercentage(undefined);
    }

    return { url: newFile.url, storageName: newFile.name };
};

const applyNewAudioFile = (videoId, file) => {
    return async (dispatch, getState) => {
        const state = getState();
        const prevAudioUrl = state.create.audioSettings.url;
        const directUploadEnabled = state.create.assetUploadingMode === ASSET_UPLOADING_MODE.DIRECT;
        const lyricsSegments = state.createPreview.present.videoSettings.lyrics.segments;

        dispatch(setProperty('audioPreparing', true));

        try {
            const extension = file.name.split('.').pop();
            const audioDuration = Math.floor(await extractAudioDuration(extension, file));

            // With Direct Upload the file is uploaded to storage immediately
            // instead of being kept in browser memory. On upload failure fall
            // back to the standard in-memory approach so the user is not blocked;
            // the save flow will upload the file as usual.
            let uploadedFile = null;
            if (directUploadEnabled) {
                try {
                    uploadedFile = await uploadAudioFileToStorage(file, extension);
                } catch (uploadError) {
                    console.error(uploadError);
                    dispatch(appOperations.openSnack(
                        'Audio upload failed. Uploading will be tried again when you save.',
                        ColoredSnackbarConstants.SNACKBAR_WARNING
                    ));
                }
            }

            const newAudioUrl = uploadedFile ? uploadedFile.url : URL.createObjectURL(file);

            dispatch(updateAudioSettings({
                url: newAudioUrl,
                displayFileName: file.name,
                duration: audioDuration,
                bassMaxMagnitude: 0,
                wideMaxMagnitude: 0,
                startTime: 0,
                endTime: audioDuration
            }));

            dispatch(setProperty('audioDuration', audioDuration));
            dispatch(setAudioDirty(true));
            dispatch(videoActions.setVideosFresh(false));

            dispatch(setProperty('unsavedAudioFile', {
                originalName: file.name,
                storageName: uploadedFile ? uploadedFile.storageName : undefined,
                extension: extension
            }));

            // Remove the lyrics that are out of audio duration.
            if (lyricsSegments.length) {
                const segments = lyricsSegments.filter((segment) => segment.endTime > audioDuration);
                if (segments.length !== 0)
                    dispatch(removeLyricsSegments(segments));
            }

            // Remove the previous audio file from memory when the new audio file is applied.
            if (isBrowserLocalUrl(prevAudioUrl))
                URL.revokeObjectURL(prevAudioUrl);

            // Start the audio analysis.
            if (!directUploadEnabled || uploadedFile)
                dispatch(analyzeAudioMaxMagnitudes(newAudioUrl));

            trackEvent('Uploaded Audio', {
                videoId: videoId ?? "unknown",
                category: AnalyticsConstants.VIDEO_CATEGORY,
                sizeMb: Number(convertToMegabytes(file.size).toFixed(2)),
                durationSeconds: audioDuration,
                extension: extension?.toLowerCase() ?? 'unknown',
                uploadMode: uploadedFile ? 'immediate' : 'deferred',
            });
        } catch (error) {
            dispatch(appOperations.failRequest(error, () => applyNewAudioFile(videoId, file), 'Audio upload failed.'));
        } finally {
            dispatch(setProperty('audioPreparing', false));
        }
    }
}

const analyzeAudioMaxMagnitudes = (audioFile) => {
    return (dispatch, getState) => {
        const { previewAudioAnalysisDisabled, optimizationModeEnabled } = getState().create;
        if (previewAudioAnalysisDisabled || optimizationModeEnabled) {
            const warningMessage = optimizationModeEnabled
                ? 'Audio analysis is disabled by Optimization Mode. The preview will not synchronize to your music, but exports will still be fully synchronized.'
                : 'Audio analysis is disabled. The preview will not synchronize to your music. Open the preview settings to enable.';

            dispatch(appOperations.openSnack(warningMessage, ColoredSnackbarConstants.SNACKBAR_WARNING));
            return;
        }

        const abortController = window.createPageAbortController;
        const successfulMessage = 'Audio analysis completed. You can now view a live preview with your audio.';
        const failedMessage = 'Audio analysis failed. Try again or change the audio file.';

        dispatch(setProperty('audioAnalyzing', true));

        analyzeCumulativeMaxMagnitudes(audioFile, (percentage) => updateDisplayLoaderPercentage(percentage), abortController).then(magnitudes => {
            dispatch(updateAudioSettings({ bassMaxMagnitude: magnitudes.bassMaxMagnitude, wideMaxMagnitude: magnitudes.wideMaxMagnitude }));
            dispatch(setProperty('audioAnalyzing', false));
            updateDisplayLoaderPercentage(undefined);
            dispatch(appOperations.openSnack(successfulMessage, ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        }).catch((error) => {
            console.error(error);
            dispatch(setProperty('audioAnalyzing', false));
            updateDisplayLoaderPercentage(undefined);
            dispatch(appOperations.openSnack(failedMessage, ColoredSnackbarConstants.SNACKBAR_ERROR));

            trackEvent('Magnitude Analysis Failed', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
                error: error
            });
        });
    }
}

const setPlayingPreview = (playingPreview) => {
    return dispatch => {
        dispatch(setProperty('playingPreview', playingPreview))

        if (playingPreview)
            trackEvent('Played Preview', { category: AnalyticsConstants.VIDEO_CATEGORY });
    }
}

const getPresets = (presetId) => {
    return (dispatch, getState) => {
        dispatch(setProperty('presetsLoading', true));

        if (presetId)
            dispatch(setProperty('videoSettingsLoading', true));

        api.get('api/VideoPresets/GetPresets').then(async (response) => {
            const accountDetails = getState().app.accountDetails;

            if (!accountDetails.email)
                await dispatch(appOperations.getAccountDetails());

            if (response.data.presets) {
                response.data.presets.forEach((p) => {
                    p.settings.visualizer.mediaSource.operationStamp = randomString();
                    p.settings.background.mediaSource.operationStamp = randomString();
                });

                dispatch(setProperty('presets', response.data.presets));

                if (presetId && response.data.presets) {
                    let preset = response.data.presets.filter(p => p.id === presetId)[0];

                    if (preset) {
                        const { subscriptionType } = getState().app.accountDetails;

                        if (preset.isPremium
                            && subscriptionType !== AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE
                            && subscriptionType !== AppConstants.LIMITLESS_SUBSCRIPTION_TYPE)
                            return;

                        dispatch(applyPreset(preset));
                        dispatch(setVideoDirty(false));
                    }
                }
            }
        }).finally(() => {
            dispatch(setProperty('presetsLoading', false));
            if (presetId)
                dispatch(setProperty('videoSettingsLoading', false));
        });
    }
}

const applyPreset = (preset) => {
    return (dispatch, getState) => {
        const state = getState();
        const currentVideoSettings = state.createPreview.present.videoSettings;
        const presetVideoSettings = createObjectCopy(preset.settings);

        // Presets are persisted through backend models that predate newer visualizer-level fields
        // (e.g. "direction"), so those keys are absent from stored presets. Merging
        // the preset over the defaults makes any field the preset omits fall back to its default
        // instead of lingering from the user's previous settings, so applying a preset fully
        // overrides them. Nested objects and the layer array that presets always carry win over the
        // defaults, so only the missing scalar fields are backfilled.
        presetVideoSettings.visualizer = {
            ...CreatePageConstants.DEFAULT_VIDEO_SETTINGS.visualizer,
            ...presetVideoSettings.visualizer,
        };

        presetVideoSettings.id = currentVideoSettings.id;

        // Assign id and operationStamp properties
        presetVideoSettings.visualizer.waveCircles.forEach((layer) => {
            layer.id = generateGuid();
            layer.operationStamp = randomString();
        });

        presetVideoSettings.text.textElements.forEach((element, i) => {
            if (typeof currentVideoSettings.text.textElements[i] !== 'undefined')
                presetVideoSettings.text.textElements[i].text = currentVideoSettings.text.textElements[i].text;
        });

        // Assign lyrics
        if (currentVideoSettings.lyrics.segments && currentVideoSettings.lyrics.segments.length)
            presetVideoSettings.lyrics.segments = currentVideoSettings.lyrics.segments;

        // Assign Logo. Use current media if it's not default or preset one
        if (!Boolean(currentVideoSettings.visualizer.mediaSource.isPreset))
            presetVideoSettings.visualizer.mediaSource = { ...currentVideoSettings.visualizer.mediaSource };

        // Assign Background. Use the current one if it's a color or gradient, or media that is not the
        // default or preset one. Colors are recognized by type because documents saved before colors
        // stopped being flagged as presets still carry isPreset: true.
        const currentBackgroundMediaSource = currentVideoSettings.background.mediaSource;
        if (isColorOrGradientSource(currentBackgroundMediaSource) || !Boolean(currentBackgroundMediaSource.isPreset))
            presetVideoSettings.background.mediaSource = { ...currentBackgroundMediaSource };

        // Preserve the YouTube animation
        presetVideoSettings.elements.youTubeCta = { ...currentVideoSettings.elements.youTubeCta };

        // Preserve the image elements: the preset dialog promises the user's images stay, and the reducer
        // takes the image element list from the incoming settings, where a preset carries none of them.
        const currentImageElements = createObjectCopy(readImageElements(currentVideoSettings));
        presetVideoSettings.elements.imageElements = currentImageElements;
        presetVideoSettings.elements.images = currentImageElements;

        // Create the local urls if the preset uses vimeo videos (will be the same for images and uploaded videos)
        createLocalMediaSourceUrls(presetVideoSettings.background.mediaSource, (localMediaSource) => {
            presetVideoSettings.background.mediaSource = localMediaSource;

            dispatch(updateVideoSettingsAction(presetVideoSettings));
            setVideoDirtyState(dispatch);
        }, null, window.createPageAbortController);

        // Close the popup with presets
        dispatch(setProperty('presetDialogOpen', false));

        trackEvent('Applied Preset', {
            presetId: preset.id,
            name: preset.name,
            category: AnalyticsConstants.VIDEO_CATEGORY,
        });
    }
}

const getMaxValues = () => {
    return dispatch => {
        axios.get('api/media/GetMaxValues').then(response => {
            dispatch(setProperty('maxValues', response.data));
        });
    }
}

const getQueuedExportsCount = () => {
    return dispatch => {
        axios.get('api/export/GetCurrentExportsCount/').then(response => {
            const runningExportsCount = response.data.runningExportsCount;
            const queuedExportsCount = response.data.queuedExportsCount;

            dispatch(setProperty('currentlyExporting', (runningExportsCount + queuedExportsCount) > 0));
            dispatch(setProperty('runningExportsCount', runningExportsCount));
            dispatch(setProperty('queuedExportsCount', queuedExportsCount));
        });
    }
}

const openExportDialog = (navigate) => async (dispatch, getState) => {
    await waitForBackgroundVideosToLoad(getState);

    dispatch(
        saveVideoProject(() => {
            // Delay before deciding what to do:
            // - gives the user a chance to see that saving has started/completed
            // - ensures the saving state is fully updated so we can check for failures
            delay(() => {
                const { videoSavingState } = getState().create;

                const hasFailed = Object.values(videoSavingState).some(
                    (value) => value?.status === 'failed'
                );

                // On failure, keep the saving panel open so the
                // user can read which step failed and hit "Try Again".
                if (hasFailed) return;

                // Success: close the saving panel, then open the export dialog shortly after.
                dispatch(setProperty('videoSavingState.savingPanelOpen', false));

                delay(() => {
                    dispatch(setProperty('exportDialogOpen', true));
                    dispatch(appOperations.getAccountDetails());
                    dispatch(setProperty('currentlyExporting', false));
                    dispatch(getQueuedExportsCount());

                    trackEvent('Opened Export Dialog', {
                        category: AnalyticsConstants.VIDEO_CATEGORY,
                    });
                }, 300);
            }, 1000);
        }, true, navigate)
    );
};

const queueExport = (videoId, videoType, success) => {
    return (dispatch, getState) => {
        const { videoSavingState } = getState().create;

        // General guard: don't queue an export from a half-saved
        // project if any saving step failed.
        const failedStep = Object.values(videoSavingState ?? {}).find(
            (step) => step?.status === VIDEO_SAVING_STEP_STATUS.FAILED
        );
        if (failedStep) {
            dispatch(appOperations.openSnack(
                failedStep.message || 'Some of your changes couldn\'t be saved. Please try again before exporting.',
                ColoredSnackbarConstants.SNACKBAR_WARNING
            ));
            return;
        }

        dispatch(setProperty('queueingExport', true));
        axios.post(`api/export/QueueExport/${videoId}/${videoType}`).then(response => {
            dispatch(exportsActions.updateExportProgress(
                response.data.exportProgress.exportId,
                { step: response.data.exportProgress.step, value: null }
            ));

            dispatch(videoActions.getVideos(0));

            if (typeof success === "function")
                success();

            trackEvent('Queued Export', {
                videoType: videoType,
                videoId: videoId,
                exportId: response.data.exportProgress.exportId,
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
        }).catch(error => {
            // Handle the app maintenance.
            const forbiddenStatusCode = 403;
            if (error.response?.status === forbiddenStatusCode) {
                dispatch(appOperations.showInformationDialog(true,
                    "Maintenance in Progress",
                    "Specterr is being updated! Exporting is temporarily unavailable. Please check back soon.",
                    "Close"
                ));
                return;
            }

            // Handle the queue limit.
            const tooManyExportsStatusCode = 429;
            if (error.response?.status === tooManyExportsStatusCode) {
                dispatch(appOperations.showInformationDialog(true,
                    "Queue is full",
                    "The limit of parallel exports has been achieved. Please wait until your active export has finished.",
                    "Close"
                ));
                return;
            }

            // Handle the unknown error.
            trackEvent('Showed Export Queue Warning Toast', {
                type: videoType,
                errorMessage: error.response.data,
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
            dispatch(appOperations.openSnack('There was an issue initiating your export. Please wait a few seconds and try again.', ColoredSnackbarConstants.SNACKBAR_WARNING));
        }).finally(() => {
            dispatch(setProperty('queueingExport', false));
        });
    }
}

const setShowAdvancedSettings = (show) => {
    return dispatch => {
        dispatch(setShowAdvancedSettingsAction(show));

        if (show)
            trackEvent('Showed Advanced Settings', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
        else
            trackEvent('Hid Advanced Settings', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
    }
}

const setShowUpdateDialog = (show) => {
    return dispatch => {
        dispatch(setShowUpdateDialogAction(show));

        if (!show)
            trackEvent('Closed Update Dialog', {
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
    }
}

const setPreviewResolution = (resolution) => {
    return dispatch => {
        dispatch(setPreviewResolutionAction(resolution));

        trackEvent('Set Preview Resolution', {
            resolution: resolution,
            screenWidth: window.innerWidth,
        });
    }
}

// Callers send their own analytics event, so the Features panel and the toolbar each report
// the toggle once under their own name.
const setOptimizationMode = (enabled) => {
    return (dispatch, getState) => {
        // The mode decides which preview work runs, so it must not change
        // under the feet of a running saving pipeline.
        if (getState().create.videoSavingState.savingInProgress)
            return;

        const wasEnabled = getState().create.optimizationModeEnabled;

        dispatch(setProperty('optimizationModeEnabled', enabled));
        saveOptimizationMode(enabled);

        if (enabled) {
            // Optimization Mode is incompatible with the memory-heavy sub-features:
            // edit history retains undo snapshots and Lazy Upload keeps picked assets
            // in browser memory until save, so both are forced off with the mode.
            const { editHistoryEnabled, assetUploadingMode } = getState().create;

            // Remember the user's own choices so disabling the mode can restore
            // them. A repeated enable would only see the forced values, so just
            // the off-to-on transition may write the snapshot.
            if (!wasEnabled)
                savePreOptimizationSettings({ editHistoryEnabled, assetUploadingMode });

            if (editHistoryEnabled)
                dispatch(setEditHistoryEnabled(false));

            if (assetUploadingMode !== ASSET_UPLOADING_MODE.DIRECT)
                dispatch(setAssetUploadingMode(ASSET_UPLOADING_MODE.DIRECT));

            // Turning the mode on is the remedy the recommendation card suggests, so the
            // failure streak starts over from a clean slate.
            dispatch(setProperty('consecutiveSaveFailures', 0));
        } else {
            const snapshot = getPreOptimizationSettings();

            if (snapshot) {
                const { editHistoryEnabled, assetUploadingMode } = getState().create;

                if (snapshot.editHistoryEnabled !== editHistoryEnabled)
                    dispatch(setEditHistoryEnabled(snapshot.editHistoryEnabled));

                if (snapshot.assetUploadingMode !== assetUploadingMode)
                    dispatch(setAssetUploadingMode(snapshot.assetUploadingMode));

                clearPreOptimizationSettings();
            }
        }
    }
}

const setEditHistoryEnabled = (enabled) => {
    return (dispatch, getState) => {
        // The setting decides what a running saving pipeline may assume about
        // the state, so it must not change mid-save.
        if (getState().create.videoSavingState.savingInProgress)
            return;

        dispatch(setProperty('editHistoryEnabled', enabled));
        saveEditHistoryEnabled(enabled);

        // The history filter is a module singleton without store access,
        // so the flag has to be mirrored into it.
        previewHistoryFilter.setEditHistoryEnabled(enabled);

        // Release the retained history entries; stopping the recording alone
        // does not free the memory they hold.
        if (!enabled)
            dispatch(clearActionsHistory());

        trackEvent('Set Edit History', {
            enabled: enabled,
            category: AnalyticsConstants.VIDEO_CATEGORY,
        });
    }
}

const setAssetUploadingMode = (mode) => {
    return (dispatch, getState) => {
        // The mode decides how resources are uploaded, so it must not change
        // under the feet of a running saving pipeline.
        if (getState().create.videoSavingState.savingInProgress)
            return;

        dispatch(setProperty('assetUploadingMode', mode));
        saveAssetUploadingMode(mode);

        // Switching to Direct Upload also remedies failing saves, so the failure
        // streak starts over from a clean slate.
        if (mode === ASSET_UPLOADING_MODE.DIRECT)
            dispatch(setProperty('consecutiveSaveFailures', 0));

        trackEvent('Set Asset Uploading Mode', {
            mode: mode,
            category: AnalyticsConstants.VIDEO_CATEGORY,
        });
    }
}

const movePlayingPreviewForward = (duration) => {
    return (dispatch, getState) => {
        const state = getState();
        const playbackTime = convertToLyricsTime(state.create.appliedPlaybackTime + duration);

        if (playbackTime <= state.create.audioDuration)
            dispatch(setProperty('appliedPlaybackTime', playbackTime));
        else
            dispatch(setProperty('appliedPlaybackTime', state.create.audioDuration));
    }
}

const movePlayingPreviewBack = (duration) => {
    return (dispatch, getState) => {
        const state = getState();
        const playbackTime = parseFloat((state.create.appliedPlaybackTime - duration).toFixed(1));

        if (playbackTime >= 0)
            dispatch(setProperty('appliedPlaybackTime', playbackTime));
        else
            dispatch(setProperty('appliedPlaybackTime', 0));
    }
}

const addLyricsSegmentToFreePlace = (newSegment, duration, addToPlaybackPosition = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const lyricsSegments = state.createPreview.present.videoSettings.lyrics.segments;

        if (addToPlaybackPosition) {
            newSegment.startTime = convertToLyricsTime(state.create.appliedPlaybackTime - (duration / 2));
            newSegment.endTime = convertToLyricsTime(newSegment.startTime + duration);
        }

        const bounds = findSpaceForSegment(lyricsSegments, newSegment.startTime, duration, state.create.audioDuration);

        if (bounds.top !== undefined && bounds.bottom !== undefined) {
            if (newSegment.startTime >= bounds.top && newSegment.endTime <= bounds.bottom) {
                dispatch(addLyricsSegment(newSegment));
            } else {
                if (newSegment.startTime < bounds.top) {
                    newSegment.startTime = bounds.top;
                    newSegment.endTime = convertToLyricsTime(bounds.top + duration);
                    dispatch(addLyricsSegment(newSegment));
                } else if (newSegment.endTime > bounds.bottom) {
                    newSegment.startTime = convertToLyricsTime(bounds.bottom - duration);
                    newSegment.endTime = bounds.bottom;
                    dispatch(addLyricsSegment(newSegment));
                }
            }
        }
    }
}

const setLyricsActionsPosition = (startTime, endTime) => {
    return (dispatch, getState) => {
        const state = getState();
        const { lyricsActionsPanelTime, audioDuration } = state.create;
        const { videoSettings } = state.createPreview.present;

        const hiddenActionsPosition = -1;
        const lyricsActionsDuration = convertToLyricsTime(endTime - startTime);
        const middlePoint = convertToLyricsTime(startTime + (lyricsActionsDuration / 2));

        // If the new time is in range of audio duration
        if (middlePoint < 0 || middlePoint > audioDuration || startTime === endTime) {
            if (lyricsActionsPanelTime !== hiddenActionsPosition) {
                dispatch(setProperty('lyricsActionsPanelTime', hiddenActionsPosition));
            }
        } else {
            const middlePointBounds = getMiddlePointBounds(videoSettings.lyrics.segments, middlePoint, audioDuration);
            const boundsDuration = convertToLyricsTime(middlePointBounds.bottom - middlePointBounds.top);

            if (middlePointBounds.top !== -1 && middlePointBounds.bottom !== -1 && boundsDuration >= lyricsActionsDuration) {
                if (startTime >= middlePointBounds.top && endTime <= middlePointBounds.bottom) {
                    if (startTime !== lyricsActionsPanelTime) {
                        dispatch(setProperty('lyricsActionsPanelTime', startTime));
                    }
                } else {
                    if (startTime < middlePointBounds.top) {
                        if (middlePointBounds.top !== lyricsActionsPanelTime) {
                            dispatch(setProperty('lyricsActionsPanelTime', middlePointBounds.top));
                        }
                    } else if (endTime > middlePointBounds.bottom) {
                        const correctedTime = middlePointBounds.bottom - lyricsActionsDuration;

                        if (correctedTime !== lyricsActionsPanelTime) {
                            dispatch(setProperty('lyricsActionsPanelTime', correctedTime));
                        }
                    }
                }
            } else if (lyricsActionsPanelTime !== hiddenActionsPosition) {
                dispatch(setProperty('lyricsActionsPanelTime', hiddenActionsPosition));
            }
        }
    }
}

const togglePlayingPreview = () => {
    // Small action to improve the performance. 
    // Play and pause the preview without property injection to a component
    return (dispatch, getState) => {
        const state = getState();
        dispatch(setProperty('playingPreview', !state.create.playingPreview));
    }
}

const addLyricsSegment = (newSegment) => {
    return (dispatch) => {
        // Assign the time stamp to know when the segment is added to the redux store.
        if (newSegment)
            newSegment.initializationTime = new Date();

        dispatch(addLyricsSegmentAction(newSegment));
        setVideoDirtyState(dispatch);
    };
}

const addLyricsSegments = (newSegments) => {
    return (dispatch) => {
        // Assign the time stamp to know when the segment is added to the redux store.
        if (newSegments)
            newSegments.forEach(segment => segment.initializationTime = new Date());

        dispatch(addLyricsSegmentsAction(newSegments));
        setVideoDirtyState(dispatch);
    };
}

const setLyricsSegmentTime = (segment, startTime, endTime, freezedDuration, audioDuration) => {
    return (dispatch) => {
        dispatch(setLyricsSegmentTimeAction(segment, startTime, endTime, freezedDuration, audioDuration));
        setVideoDirtyState(dispatch);
    };
}

const removeLyricsSegment = (lyricsSegment) => {
    return (dispatch) => {
        dispatch(removeLyricsSegmentAction(lyricsSegment));
        setVideoDirtyState(dispatch);
    };
}

const removeLyricsSegments = (lyricsSegments) => {
    return (dispatch) => {
        dispatch(removeLyricsSegmentsAction(lyricsSegments));
        setVideoDirtyState(dispatch);
    };
}

const updateLyricsSegment = (index, properties) => {
    return (dispatch) => {
        dispatch(updateLyricsSegmentAction(index, properties));
        setVideoDirtyState(dispatch);
    };
}

const updateVideoSettings = (newSettings, testDirty = true) => {
    return (dispatch) => {
        // Assign the time stamp to know when the lyrics segment is added to the redux store.
        if (newSettings && newSettings.lyrics && newSettings.lyrics.segments) {
            newSettings.lyrics.segments.forEach(segment => {
                segment.initializationTime = !segment.initializationTime ? new Date() : segment.initializationTime;
            });
        }

        dispatch(updateVideoSettingsAction(newSettings, testDirty));
        setVideoDirtyState(dispatch);
    };
}

const updateTextElement = (index, properties) => {
    return (dispatch) => {
        dispatch(updateTextElementAction(index, properties));
        setVideoDirtyState(dispatch);
    };
}

const addTextElement = () => {
    return (dispatch) => {
        dispatch(addTextElementAction());
        setVideoDirtyState(dispatch);
    };
}

const removeTextElement = (index) => {
    return (dispatch) => {
        dispatch(removeTextElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const duplicateTextElement = (index) => {
    return (dispatch) => {
        dispatch(duplicateTextElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const updateParticleElement = (index, properties) => {
    return (dispatch) => {
        dispatch(updateParticleElementAction(index, properties));
        setVideoDirtyState(dispatch);
    };
}

const addParticleElement = () => {
    return (dispatch) => {
        dispatch(addParticleElementAction());
        setVideoDirtyState(dispatch);
    };
}

const removeParticleElement = (index) => {
    return (dispatch) => {
        dispatch(removeParticleElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const duplicateParticleElement = (index) => {
    return (dispatch) => {
        dispatch(duplicateParticleElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const updateImageElement = (index, properties) => {
    return (dispatch, getState) => {
        // With edit history disabled the replaced picture's local object url is released
        // right away; with history enabled an undo could still need it.
        if (properties.mediaSource && !getState().create.editHistoryEnabled) {
            const prevUrl = getState().createPreview.present.videoSettings.elements.images?.[index]?.mediaSource?.data?.url;
            if (isBrowserLocalUrl(prevUrl) && prevUrl !== properties.mediaSource.data?.url)
                URL.revokeObjectURL(prevUrl);
        }

        dispatch(updateImageElementAction(index, properties));
        setVideoDirtyState(dispatch);
    };
}

const addImageElement = (mediaSource, properties = {}) => {
    return (dispatch) => {
        dispatch(addImageElementAction(mediaSource, properties));
        setVideoDirtyState(dispatch);
    };
}

const removeImageElement = (index) => {
    return (dispatch) => {
        dispatch(removeImageElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const duplicateImageElement = (index) => {
    return (dispatch) => {
        dispatch(duplicateImageElementAction(index));
        setVideoDirtyState(dispatch);
    };
}

const changeImageElementOrder = (firstIndex, secondIndex) => {
    return (dispatch) => {
        dispatch(changeImageElementOrderAction(firstIndex, secondIndex));
        setVideoDirtyState(dispatch);
    };
}

const undo = () => {
    return (dispatch) => {
        dispatch(undoAction());
        setVideoDirtyState(dispatch);
    };
}

const redo = () => {
    return (dispatch) => {
        dispatch(redoAction());
        setVideoDirtyState(dispatch);
    };
}

const updateBackgroundSettings = (properties, initializationProcess = false) => {
    return (dispatch, getState) => {
        const state = getState();

        const hlsAdjustment = state.createPreview.present.videoSettings.background.hlsAdjustment;
        const videoAcceleration = state.createPreview.present.videoSettings.background.videoAcceleration;
        const prevMediaSource = state.createPreview.present.videoSettings.background.mediaSource;
        const mediaSource = properties.mediaSource;
        const backgroundSettings = { ...properties };

        // Cleanup previous resources in memory
        if (mediaSource && prevMediaSource) {
            for (const key in prevMediaSource.data) {
                if (Object.prototype.hasOwnProperty.call(prevMediaSource.data, key)) {
                    const value = prevMediaSource.data[key];
                    if (isBrowserLocalUrl(value))
                        URL.revokeObjectURL(value)
                }
            }
        }

        if (mediaSource)
            previewHistoryFilter.disableHistoryTracking();

        // Disables hlsAdjustment when changing the background
        if (hlsAdjustment.enabled && properties.mediaSource)
            backgroundSettings.hlsAdjustment = { ...hlsAdjustment, enabled: false };

        // Disables videoAcceleration when changing the background to a non-video (not during project load).
        // Switching between videos preserves the toggle so the user doesn't have to re-enable it.
        if (properties.mediaSource && !initializationProcess && properties.mediaSource.mediaResourceType !== MEDIA_RESOURCE_TYPE.VIDEO)
            backgroundSettings.videoAcceleration = { ...videoAcceleration, enabled: false };

        // Resets media-only effects when switching to a color/gradient background,
        // since rotation, mirroring and movement have no meaning for a flat fill.
        if (properties.mediaSource && !initializationProcess && isColorOrGradientSource(properties.mediaSource)) {
            backgroundSettings.rotation = 0;
            backgroundSettings.reflection = { type: BACKGROUND_REFLECTION_TYPE.NONE, direction: 1 };
            backgroundSettings.shakeEnabled = false;
            backgroundSettings.motionBlurEnabled = false;
            backgroundSettings.drift = { enabled: false };
            backgroundSettings.slowZoom = { enabled: false };
        }

        dispatch(updateBackgroundSettingsAction(backgroundSettings));

        if (mediaSource)
            previewHistoryFilter.enableHistoryTracking();

        if (!initializationProcess)
            setVideoDirtyState(dispatch);
    };
}

const updateLyricsStyle = (properties) => {
    return (dispatch) => {
        dispatch(updateLyricsStyleAction(properties));
        setVideoDirtyState(dispatch);
    };
}

const addWaveLayer = () => {
    return (dispatch) => {
        dispatch(addWaveLayerAction());
        setVideoDirtyState(dispatch);
    };
}

const updateWaveLayer = (index, properties) => {
    return (dispatch) => {
        dispatch(updateWaveLayerAction(index, properties));
        setVideoDirtyState(dispatch);
    };
}

const removeWaveLayer = (index) => {
    return (dispatch) => {
        dispatch(removeWaveLayerAction(index));
        setVideoDirtyState(dispatch);
    };
}

const duplicateWaveLayer = (index) => {
    return (dispatch) => {
        dispatch(duplicateWaveLayerAction(index));
        setVideoDirtyState(dispatch);
    };
}

const changeWaveLayerOrder = (firstIndex, secondIndex) => {
    return (dispatch) => {
        dispatch(changeWaveLayerOrderAction(firstIndex, secondIndex));
        setVideoDirtyState(dispatch);
    };
}

const updateVisualizerLayersType = (layoutType) => {
    return (dispatch) => {
        dispatch(updateVisualizerLayersTypeAction(layoutType));
        setVideoDirtyState(dispatch);
    }
}

const updateVisualizerShape = (shape) => {
    return (dispatch, getState) => {
        const state = getState();
        const waveCircles = state.createPreview.present.videoSettings.visualizer.waveCircles;

        waveCircles.forEach((waveCircle, index) => {
            dispatch(updateWaveLayerCustomOptionsAction(index, "reflectionType", REFLECTION_TYPE.NONE));
        });

        dispatch(updateVisualizerSettings({
            shape: shape,
            reflectionType: REFLECTION_TYPE.NONE
        }));
    }
}

const updateWaveLayerCustomOptions = (index, key, value) => {
    return (dispatch) => {
        dispatch(updateWaveLayerCustomOptionsAction(index, key, value))
        setVideoDirtyState(dispatch);
    }
}

const updateWaveLayerSpinSettings = (index, key, value) => {
    return (dispatch) => {
        dispatch(updateWaveLayerSpinSettingsAction(index, key, value))
        setVideoDirtyState(dispatch);
    }
}

const updateVisualizerSettings = (properties, initializationProcess = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const mediaSource = properties.mediaSource;
        const prevMediaSource = state.createPreview.present.videoSettings.visualizer.mediaSource;

        // Cleanup previous resources in memory
        if (mediaSource && isBrowserLocalUrl(prevMediaSource.data.url)) {
            URL.revokeObjectURL(prevMediaSource.data.url);
        }

        if (mediaSource)
            previewHistoryFilter.disableHistoryTracking();

        dispatch(updateVisualizerSettingsAction(properties));

        if (mediaSource)
            previewHistoryFilter.enableHistoryTracking();


        if (!initializationProcess)
            setVideoDirtyState(dispatch);
    };
}

const clearLyrics = () => {
    return (dispatch) => {
        dispatch(clearLyricsAction());
        setVideoDirtyState(dispatch);
    };
}

const updateElementsSettings = (properties) => {
    return (dispatch) => {
        dispatch(updateElementsSettingsAction(properties));
        setVideoDirtyState(dispatch);
    };
}

const updateAspectRatio = (aspectRatio) => {
    return (dispatch) => {
        dispatch(updateAspectRatioAction(aspectRatio));
        setVideoDirtyState(dispatch);
    };
}

const resetVideoSettings = () => {
    return (dispatch) => {
        dispatch(resetVideoSettingsAction());
    };
}

const showCriticalOperationProgressWarning = () => {
    return (dispatch) => {
        dispatch(appOperations.openSnack(
            'Execution temporarily disabled. Try again in a few seconds.',
            ColoredSnackbarConstants.SNACKBAR_WARNING
        ));
    };
}

const isVideoExportsQueued = () => {
    return (dispatch, getState) => {
        const state = getState();
        const videoExports = state.exports.videoExports;
        const queuedExports = videoExports.some(x => x.status == EXPORT_STATUSES.PENDING);
        const runningExports = videoExports.some(x => x.status == EXPORT_STATUSES.RUNNING);
        const activeExports = runningExports || queuedExports;

        if (activeExports) {
            dispatch(appOperations.showInformationDialog(true, "", "You have active exports. Please wait until they finish or make a copy of the video."))
        }

        return activeExports;
    }
}

const enqueueLyricExtractionJob = (videoId) => {
    return async (dispatch) => {
        if (!videoId) {
            dispatch(appOperations.openSnack('The video id is missing.', ColoredSnackbarConstants.SNACKBAR_WARNING));
            return;
        }

        dispatch(setProperty('lyricExtractionProcessing', true));
        dispatch(setProperty('lyricExtractionJobId', null));

        try {
            const response = await axios.post('/api/AudioLyrics/EnqueueLyricExtractionJob', { videoId });
            const jobId = response?.data?.jobId;
            if (jobId)
                dispatch(setProperty('lyricExtractionJobId', jobId));

            dispatch(appOperations.openSnack('Lyric extraction started. Please wait...', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        } catch (error) {
            const message = error?.response?.data || 'Failed to start Auto Detect.';
            dispatch(appOperations.openSnack(message, ColoredSnackbarConstants.SNACKBAR_ERROR));
            dispatch(setProperty('lyricExtractionProcessing', false));
            dispatch(setProperty('lyricExtractionJobId', null));
        }
    }
}

const cancelLyricExtractionJob = (jobId) => {
    return () => {
        try {
            if (!jobId)
                return;

            axios.post('/api/AudioLyrics/CancelLyricExtractionJob', { jobId });
        } catch {
            console.error('Failed to cancel lyric extraction job');
        }
    }
}

const getGoogleFonts = (skip = 0, take = 50) => {
    return async () => {
        return axios.get(`api/GoogleFonts/GetFonts?skip=${encodeURIComponent(skip)}&take=${encodeURIComponent(take)}`);
    };
};

const searchGoogleFonts = (query, skip = 0, take = 20) => {
    return async () => {
        return axios.get(`api/GoogleFonts/SearchFonts?query=${encodeURIComponent(query)}&skip=${encodeURIComponent(skip)}&take=${encodeURIComponent(take)}`);
    };
};

const applyDetectedLyricsSegments = (segments) => {

    return (dispatch) => {
        if (!segments)
            return;

        dispatch(setPreviewPropertyWithoutTracking('videoSettings.lyrics.segments', segments));
        dispatch(setPreviewPropertyWithoutTracking('lyricsSettingsUpdateTimeStamp', Date.now()));
        setVideoDirtyState(dispatch);
    };
}

export default {
    renameAudioFile,
    setProperty,
    setPreviewProperty,
    getQueuedExportsCount,
    isVideoExportsQueued,
    updateWaveLayerCustomOptions,
    setPreviewPropertyWithoutTracking,
    updateVideoSettings,
    updateAudioSettings,
    getVideoSettings,
    setPlayingPreview,
    saveVideoProject,
    applyNewAudioFile,
    uploadCroppedImageDirectly,
    uploadBackgroundVideoDirectly ,
    uploadImageElementDirectly,
    openExportDialog,
    getPresets,
    applyPreset,
    getMaxValues,
    queueExport,
    updateVisualizerShape,
    updateVisualizerLayersType,
    updateVisualizerReflectionType,
    applyAudioSettingsForPresetMode,
    setShowAdvancedSettings,
    setShowUpdateDialog,
    setPreviewResolution,
    updateLyricsSegment,
    removeLyricsSegment,
    setLyricsActionsPosition,
    setLyricsSegmentTime,
    addLyricsSegment,
    addLyricsSegments,
    movePlayingPreviewForward,
    movePlayingPreviewBack,
    addLyricsSegmentToFreePlace,
    togglePlayingPreview,
    undo,
    redo,
    clearActionsHistory,
    updateTextElement,
    addTextElement,
    removeTextElement,
    duplicateTextElement,
    updateParticleElement,
    addParticleElement,
    removeParticleElement,
    duplicateParticleElement,
    updateImageElement,
    addImageElement,
    removeImageElement,
    duplicateImageElement,
    changeImageElementOrder,
    updateWaveLayerSpinSettings,
    updateBackgroundSettings,
    updateLyricsStyle,
    addWaveLayer,
    updateWaveLayer,
    removeWaveLayer,
    duplicateWaveLayer,
    changeWaveLayerOrder,
    updateVisualizerSettings,
    clearLyrics,
    selectOrDeselectSegments,
    updateElementsSettings,
    updateAspectRatio,
    resetVideoSettings,
    showCriticalOperationProgressWarning,
    removeLyricsSegments,
    analyzeAudioMaxMagnitudes,
    setOptimizationMode,
    setEditHistoryEnabled,
    setAssetUploadingMode,
    enqueueLyricExtractionJob,
    cancelLyricExtractionJob,
    applyDetectedLyricsSegments,
    getGoogleFonts,
    searchGoogleFonts
};